import { useState, useEffect, useCallback } from 'react';
import { profileApi } from '../api/client';

export type Lens = 'buyer' | 'seller';

const CACHE_KEY = 'fti_lens';

function readCache(): Lens {
  try {
    const v = localStorage.getItem(CACHE_KEY);
    return v === 'seller' ? 'seller' : 'buyer';
  } catch {
    return 'buyer';
  }
}

/**
 * useLens — the current user's buyer/seller lens (org_type).
 *
 * - Seeds from a localStorage cache for instant first paint, then reconciles
 *   with the server profile (/profile).
 * - setLens optimistically updates the UI + cache and persists to the server;
 *   on failure it reverts.
 *
 * Buyer is always the safe default so the existing experience is unchanged
 * for anyone without a seller profile.
 */
export function useLens() {
  const [lens, setLensState] = useState<Lens>(readCache);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    profileApi
      .get()
      .then((res) => {
        const ot = res?.profile?.org_type;
        const next: Lens = ot === 'seller' ? 'seller' : 'buyer';
        if (!cancelled) {
          setLensState(next);
          try { localStorage.setItem(CACHE_KEY, next); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* keep cached/default on error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const setLens = useCallback(async (next: Lens) => {
    const prev = lens;
    setLensState(next);
    try { localStorage.setItem(CACHE_KEY, next); } catch { /* ignore */ }
    try {
      await profileApi.update({ org_type: next });
    } catch {
      // Revert on failure.
      setLensState(prev);
      try { localStorage.setItem(CACHE_KEY, prev); } catch { /* ignore */ }
      throw new Error('Could not switch lens. Please try again.');
    }
  }, [lens]);

  return { lens, setLens, loading };
}
